'use client';

import { motion } from 'motion/react';
import { useLocale } from './LocaleProvider';
import { dict, t } from '@/lib/i18n';
import { verifiedTestimonials } from '@/lib/testimonials';

// Stats updated post-v3.x deploys (2026-04-28):
// - 401k authentic reference images embedded for visual retrieval
// - 4-model AI vision ensemble (GPT-5.4 + Sonnet 4.6 + Gemini 3.1 Pro + Llama 4 Maverick)
// - retailers/gccSites unchanged from earlier product
// - kept "0 data tracked" as the privacy commitment
const stats = [
  { value: '401k', label: dict.socialProof.stats.authenticReferences },
  { value: '4', label: dict.socialProof.stats.ensembleModels },
  { value: '42+', label: dict.socialProof.stats.retailers },
  { value: '0', label: dict.socialProof.stats.dataTracking },
];

export function SocialProof() {
  const { locale } = useLocale();
  const quotes = verifiedTestimonials();
  const hasRealQuotes = quotes.length >= 1;

  return (
    <section className="relative py-14 sm:py-20 border-t border-white/5 bg-ink-800/40">
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="group text-center p-6 rounded-luxe bg-white/[0.02] border border-white/5 transition-[border-color,background-color,transform] duration-300 ease-out hover:border-white/10 hover:bg-white/[0.035] hover:translate-y-[-2px]"
            >
              <div className="font-serif text-4xl text-gold-soft font-bold mb-2 tnum transition-transform duration-300 ease-out group-hover:scale-105">
                {stat.value}
              </div>
              <div className="text-sm text-fog-500">
                {t(stat.label, locale)}
              </div>
            </motion.div>
          ))}
        </div>

        {hasRealQuotes ? (
          /* Real, verified quotes */
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-gold-soft font-semibold mb-6">
              <span className="w-1 h-1 rounded-full bg-gold-soft" />
              {t(dict.socialProof.earlyAccess, locale)}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {quotes.map((quote, i) => (
                <motion.blockquote
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 + i * 0.1 }}
                  className="rounded-luxe bg-ink-800/80 border border-white/10 p-5 text-start"
                >
                  <p className="text-fog-300 text-sm leading-relaxed mb-3">
                    &ldquo;{t(quote.text, locale)}&rdquo;
                  </p>
                  <footer className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gold/30 to-gold-deep/30 grid place-items-center text-xs text-gold-soft font-bold">
                      {quote.initials}
                    </div>
                    <div>
                      <div className="text-xs text-fog-100 font-medium">{quote.name}</div>
                      <div className="text-xs text-fog-700">{t(quote.role, locale)}</div>
                    </div>
                  </footer>
                </motion.blockquote>
              ))}
            </div>
          </div>
        ) : (
          /* Honest empty state — invites beta testers instead of faking reviews */
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mx-auto text-center rounded-luxe bg-white/[0.02] border border-white/5 p-8 md:p-10"
          >
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-gold-soft font-semibold mb-4">
              <span className="w-1 h-1 rounded-full bg-gold-soft" />
              {locale === 'ar' ? 'مرحلة البيتا' : 'Beta launch'}
            </div>
            <h3 className="font-serif text-cream text-2xl md:text-3xl mb-3">
              {locale === 'ar'
                ? 'كن من أوائل المستخدمين. شاركنا رأيك.'
                : 'Be among our first users. Tell us what you think.'}
            </h3>
            <p className="text-fog-400 text-sm leading-relaxed max-w-lg mx-auto">
              {locale === 'ar'
                ? 'لن نضع آراء وهمية هنا. عندما يوافق مستخدمون حقيقيون على الاستشهاد بهم، ستظهر كلماتهم في هذا المكان — حرفيًا.'
                : 'We will never put fake reviews here. When real users give us written consent, their words will appear in this space — verbatim.'}
            </p>
            <a
              href="#newsletter"
              className="press-lift group inline-flex items-center gap-3 mt-6 px-6 py-3 rounded-full bg-gradient-to-r from-gold-deep to-gold text-obsidian-900 font-bold text-sm tracking-wide hover:shadow-glow-gold transition-[box-shadow,transform] duration-200 ease-out"
            >
              {locale === 'ar' ? 'انضم للبيتا' : 'Join the beta waitlist'}
              <span aria-hidden className={`arrow-slide ${locale === 'ar' ? 'rotate-180' : ''}`}>→</span>
            </a>
          </motion.div>
        )}
      </div>
    </section>
  );
}
